// src/pages/WelcomeWizard.tsx
import { Trans, useTranslation } from "react-i18next";
import type { ParseKeys, TFunction } from "i18next";
import UiLanguageSelect from "../components/UiLanguageSelect";
import React, {
  useEffect,
  useState,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import StepHeader from "../components/StepHeader";
import StepProgress from "../components/StepPorgress";
import { TrashIcon } from "@heroicons/react/24/solid";
import { apiGet, apiPost } from "../api/client";
import { getStripeStatus } from "../api/stripe";
import { Button } from "../components/Button";
import { formatNumber } from "../utils/formatNumber";
import { formatMMDDYYYYFromYMD, ymdTodayLocal } from "../utils/dateUtils";
import {
  welcomeWizardMethodCreateBody,
  wizardStorageKey,
  readWizardRestoreSnapshot,
} from "../utils/welcomeWizardPosting";
import { A } from "../styles/appleClasses";
import { postLoginPath } from "../utils/upgradeIntent";
import {
  resolveOpeningInventoryAttempt,
  clearOpeningInventoryAttempt,
} from "../utils/openingInventoryIdempotency";

// Local authored outcomes retain identity until render; raw server details stay raw.
// These values are never included in the persisted wizard snapshot.
type UiKey = ParseKeys<"translation">;
type WizardMessage =
  | { kind: "local"; key: UiKey; values?: Record<string, string | number>; detail?: WizardMessage }
  | { kind: "raw"; value: string };
function localMessage(key: UiKey, values?: Record<string, string | number>, detail?: WizardMessage): WizardMessage {
  return { kind: "local", key, values, detail };
}
class WizardLocalError extends Error {
  constructor(readonly key: UiKey, readonly values?: Record<string, string | number>) {
    super(key);
  }
}
function errorMessage(error: any, fallback: UiKey): WizardMessage {
  if (error?.response?.data?.detail) return { kind: "raw", value: error.response.data.detail };
  if (error instanceof WizardLocalError) return localMessage(error.key, error.values);
  return error?.message ? { kind: "raw", value: error.message } : localMessage(fallback);
}
function messageText(message: WizardMessage, t: TFunction): string {
  if (message.kind === "raw") return message.value;
  return t(message.key, {
    ...message.values,
    ...(message.detail ? { detail: messageText(message.detail, t) } : {}),
  });
}

// AppleStyle Select
interface AppleSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode;
}
function AppleSelect({
  value,
  onChange,
  children,
  ...props
}: AppleSelectProps) {
  return (
    <div className="relative">
      <select
        className={[
          "w-full h-10 px-4 rounded-xl border border-gray-200 bg-white",
          "text-[13px] font-normal text-gray-700",
          "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition",
          "appearance-none",
        ].join(" ")}
        value={value}
        onChange={onChange}
        {...props}
      >
        {children}
      </select>

      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
          <path
            d="M7 8l3 3 3-3"
            stroke="#888"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

type AccountKind = "cash" | "bank";
interface Account {
  id: number;
  server_id?: number;
  kind: AccountKind;
  name: string;
  initial_balance: number;
}

type BusinessMode = "new_business" | "existing_business";

type PaymentMethod = {
  name: string;
  // FEE-RATE-SEMANTICS-1 Phase 2B: this local field is a PERCENT value (what the
  // user typed in "Charge Rate (%)"), submitted via the fee_rate_percent channel
  // -- never divided by 100, never a bare fee_rate.
  fee_rate_percent?: number;
  fixed_fee?: number;
  type: string;
  account_id: number;

  // Online provider metadata
  provider?: "stripe" | "none" | string;
  provider_method?: "auto" | "paylink" | string;
  online?: boolean;
};

type ConnectStartResp = {
  url?: string;
  connected?: boolean;
  account_id?: string | null;
};

const labelClass = `block mb-1 ${A.label}`;

const inputClass =
  "w-full h-10 px-4 rounded-xl border border-gray-200 bg-white " +
  "text-[13px] font-normal text-gray-700 " +
  "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition";

const cardClass = `${A.card} p-6 mt-4`;
const helperTextClass = "text-[13px] font-normal text-gray-700 text-center";

// buttons (styling only)
const primaryBtnClass =
  "w-full h-10 rounded-xl bg-appleblue text-white text-[13px] font-medium " +
  "hover:bg-appleblueD transition focus:outline-none focus:ring-2 focus:ring-appleblueD " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

const secondaryBtnClass =
  "w-full h-10 rounded-xl bg-white text-gray-700 text-[13px] font-medium border border-gray-200 " +
  "hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-appleblueD " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

const iconBtnClass = `${A.iconBtn} h-8 w-8`;

const paymentMethodOptions = [
  { name: "Cash", type: "cash" },
  { name: "Bank Transfer", type: "bank" },
  { name: "Visa/MasterCard", type: "digital" },
  { name: "AMEX", type: "digital" },
  { name: "Alipay", type: "digital" },
  { name: "WeChat Pay", type: "digital" },
  { name: "On Account", type: "receivable" },
  { name: "Other", type: "other" },
];

const paymentOptionKeys: Partial<Record<string, UiKey>> = {
  Cash: "balanceSheet.cash",
  "Bank Transfer": "paymentMethods.options.bankTransfer",
  "Visa/MasterCard": "paymentMethods.options.visaMasterCard",
  Alipay: "paymentMethods.options.alipay",
  "WeChat Pay": "paymentMethods.options.weChatPay",
  "On Account": "sales.list.onAccount",
  Other: "paymentMethods.options.other",
};

const industryOptions = [
  { id: "fnb", labelKey: "onboarding.industries.fnb" },
  { id: "retail", labelKey: "companyProfile.industries.retail" },
  { id: "services", labelKey: "onboarding.industries.services" },
  { id: "professional", labelKey: "onboarding.industries.professional" },
  { id: "online_digital", labelKey: "onboarding.industries.onlineDigital" },
  { id: "other", labelKey: "paymentMethods.options.other" },
] as const;

const STRIPE_WIZARD_DEPOSIT_KEY = "rb_wizard_pending_stripe_deposit_account";

// --- Bell Icon ---
function BellIcon() {
  return (
    <svg
      className="w-6 h-6 text-appleblue mr-2 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

type CoachMarkProps = {
  message: string;
  onSkip: () => void;
};

function CoachMark({ message, onSkip }: CoachMarkProps) {
  const { t } = useTranslation();
  return (
    <div
      className={[
        "max-w-xl mx-auto mb-4 px-4 py-3 sm:px-5 sm:py-3",
        "rounded-2xl border border-appleblue/20 bg-appleblue/10",
        "flex items-center gap-3 sm:flex-row flex-col",
      ].join(" ")}
    >
      <span className="flex-shrink-0 hidden xs:inline sm:inline">
        <BellIcon />
      </span>

      <span className="flex-1 text-[13px] font-medium text-center sm:text-left text-gray-700">
        {message}
      </span>

      <Button
        type="button"
        variant="secondary"
        minWidth="auto"
        className="px-3 h-9 rounded-xl"
        onClick={onSkip}
      >
        {t("onboarding.common.skipTour")}
      </Button>
    </div>
  );
}

type MigrationImportSummary = {
  created_count?: number;
  updated_count?: number;
  skipped_count?: number;
  total_amount?: number;
  errors?: Array<{ row?: number; reason?: string }>;
};

type MigrationImportKind =
  | "customers"
  | "vendors"
  | "products"
  | "ar"
  | "ap"
  | "inventory"
  | "fixed_assets"
  | "prepaids"
  | "deposits";

type MigrationColumn = {
  key: string;
  labelKey: UiKey;
  placeholder?: string;
  numeric?: boolean;
};

type AccountRow = {
  id: number;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type CustomerRow = {
  id: number;
  name: string;
};

type VendorRow = {
  id: number;
  name: string;
};

const migrationImportSections: Array<{
  kind: MigrationImportKind;
  titleKey: UiKey;
  helperKey: UiKey;
  columns: MigrationColumn[];
}> = [
  {
    kind: "customers",
    titleKey: "navigation.customers",
    helperKey: "onboarding.import.help.customers",
    columns: [
      { key: "name", labelKey: "directory.common.name" },
      { key: "email", labelKey: "directory.common.email" },
      { key: "phone", labelKey: "invoiceSending.phone" },
    ],
  },
  {
    kind: "vendors",
    titleKey: "navigation.vendors",
    helperKey: "onboarding.import.help.vendors",
    columns: [
      { key: "name", labelKey: "directory.common.name" },
      { key: "email", labelKey: "directory.common.email" },
      { key: "phone", labelKey: "invoiceSending.phone" },
    ],
  },
  {
    kind: "products",
    titleKey: "navigation.products",
    helperKey: "onboarding.import.help.products",
    columns: [
      { key: "name", labelKey: "directory.common.name" },
      { key: "sku", labelKey: "directory.products.sku" },
      { key: "unit_cost", labelKey: "directory.products.cost", numeric: true },
      { key: "selling_price", labelKey: "onboarding.import.columns.salePrice", numeric: true },
    ],
  },
  {
    kind: "ar",
    titleKey: "accountNames.accountsReceivable",
    helperKey: "onboarding.import.help.ar",
    columns: [
      { key: "customer_name", labelKey: "directory.common.name" },
      { key: "amount", labelKey: "sales.common.amount", numeric: true },
    ],
  },
  {
    kind: "ap",
    titleKey: "accountNames.accountsPayable",
    helperKey: "onboarding.import.help.ap",
    columns: [
      { key: "vendor_name", labelKey: "directory.common.name" },
      { key: "amount", labelKey: "sales.common.amount", numeric: true },
    ],
  },
  {
    kind: "inventory",
    titleKey: "directory.products.inventory",
    helperKey: "onboarding.import.help.inventory",
    columns: [
      { key: "product_name", labelKey: "directory.products.product" },
      { key: "sku", labelKey: "directory.products.sku" },
      { key: "qty", labelKey: "purchases.errors.quantityField", numeric: true },
      { key: "unit_cost", labelKey: "purchases.edit.unitCost", numeric: true },
      { key: "selling_price", labelKey: "onboarding.import.columns.salePrice", numeric: true },
    ],
  },
  {
    kind: "fixed_assets",
    titleKey: "expenses.tabs.fixedAssets",
    helperKey: "onboarding.import.help.fixedAssets",
    columns: [
      { key: "asset_name", labelKey: "directory.common.name" },
      { key: "net_book_value", labelKey: "onboarding.import.columns.netBookValue", numeric: true },
      {
        key: "remaining_life_months",
        labelKey: "onboarding.import.columns.remainingMonths",
        numeric: true,
      },
    ],
  },
  {
    kind: "prepaids",
    titleKey: "expenses.tabs.prepaids",
    helperKey: "onboarding.import.help.prepaids",
    columns: [
      { key: "vendor_name", labelKey: "directory.common.name" },
      { key: "amount", labelKey: "sales.common.amount", numeric: true },
    ],
  },
  {
    kind: "deposits",
    titleKey: "expenses.tabs.deposits",
    helperKey: "onboarding.import.help.deposits",
    columns: [
      { key: "vendor_name", labelKey: "directory.common.name" },
      { key: "amount", labelKey: "sales.common.amount", numeric: true },
    ],
  },
];

function normalizeHeader(v: string) {
  return (v || "").trim().toLowerCase().replace(/\s+/g, "_");
}

function splitPasteLine(line: string) {
  const delimiter = line.includes("\t") ? "\t" : ",";
  return line.split(delimiter).map((cell) => cell.trim());
}

function parsePasteRows(text: string, columns: MigrationColumn[]) {
  const lines = (text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  let start = 0;
  const first = splitPasteLine(lines[0]).map(normalizeHeader);
  const expected = columns.map((c) => c.key);

  const looksLikeHeader =
    first.length > 0 && first.every((cell) => expected.includes(cell));

  if (looksLikeHeader) start = 1;

  const rows: Record<string, any>[] = [];

  for (const line of lines.slice(start)) {
    const cells = splitPasteLine(line);
    const row: Record<string, any> = {};

    columns.forEach((col, idx) => {
      const raw = cells[idx] ?? "";
      if (col.numeric) {
        const cleaned = raw.replace(/[$,\s]/g, "");
        row[col.key] = cleaned === "" ? 0 : Number(cleaned);
      } else {
        row[col.key] = raw;
      }
    });

    const hasAnyValue = Object.values(row).some(
      (value) => String(value ?? "").trim() !== "",
    );

    if (hasAnyValue) rows.push(row);
  }

  return rows;
}

function migrationPath(kind: MigrationImportKind, migrationDate: string) {
  if (kind === "customers") return "/migration/customers";
  if (kind === "vendors") return "/migration/vendors";
  if (kind === "products") return "/migration/products";
  if (kind === "ar")
    return `/migration/ar-list?date=${encodeURIComponent(migrationDate)}`;
  if (kind === "ap")
    return `/migration/ap-list?date=${encodeURIComponent(migrationDate)}`;
  if (kind === "inventory")
    return `/migration/inventory-list?date=${encodeURIComponent(migrationDate)}`;
  if (kind === "fixed_assets") return "/migration/opening-fixed-asset";
  if (kind === "prepaids") return "/migration/opening-prepaid";
  if (kind === "deposits") return "/migration/opening-deposit";
  return `/migration/inventory-list?date=${encodeURIComponent(migrationDate)}`;
}

const FIXED_ASSET_TYPE_TO_ACCOUNT_NAME: Record<string, string> = {
  computer: "Fixed Asset – Computer",
  "equipment & fixtures": "Fixed Asset – Equipment & Fixtures",
  "equipment and fixtures": "Fixed Asset – Equipment & Fixtures",
  "leasehold improvements": "Fixed Asset – Leasehold Improvements",
  "other fixed assets": "Fixed Asset – Other",
};

function normKey(v: unknown): string {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

function firstReferenceBankAccountId(accounts: AccountRow[]): number | null {
  const found = accounts.find((a) => {
    const type = normKey(a.type);
    const kind = normKey(a.kind);
    return (
      type === "asset" && (kind === "cash" || kind === "bank") && !a.is_system
    );
  });

  return found?.id ? Number(found.id) : null;
}

function fixedAssetAccountIdForType(
  accounts: AccountRow[],
  assetType: string,
): number | null {
  const accountName = FIXED_ASSET_TYPE_TO_ACCOUNT_NAME[normKey(assetType)];
  if (!accountName) return null;

  const found = accounts.find((a) => normKey(a.name) === normKey(accountName));
  return found?.id ? Number(found.id) : null;
}

function MigrationPasteCard({
  kind,
  title,
  helper,
  columns,
  migrationDate,
  companyId,
  wizardAccounts,
  ensureWizardAccounts,
}: {
  kind: MigrationImportKind;
  title: string;
  helper: string;
  columns: MigrationColumn[];
  migrationDate: string;
  companyId?: string;
  wizardAccounts: Account[];
  ensureWizardAccounts: () => Promise<Account[]>;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<MigrationImportSummary | null>(null);
  const [error, setError] = useState<WizardMessage | null>(null);

  const rows = parsePasteRows(text, columns);
  const previewRows = rows;

  const placeholder = columns.map((c) => c.key).join("    ");

  function companyQuery(suffix: string = "") {
    return companyId ? `?company_id=${companyId}${suffix}` : "";
  }

  async function getMigrationReferenceBankAccountId(): Promise<
    number | undefined
  > {
    let bankAccountId = wizardAccounts.find(
      (a) => a.server_id && (a.kind === "cash" || a.kind === "bank"),
    )?.server_id;

    if (bankAccountId) return bankAccountId;

    const ensuredAccounts = await ensureWizardAccounts();

    bankAccountId = ensuredAccounts.find(
      (a) => a.server_id && (a.kind === "cash" || a.kind === "bank"),
    )?.server_id;

    if (bankAccountId) return bankAccountId;

    const serverAccounts = await apiGet<any[]>(`/accounts${companyQuery()}`);

    const fallback = (serverAccounts || []).find(
      (a) =>
        String(a.type || "").toLowerCase() === "asset" &&
        ["cash", "bank"].includes(String(a.kind || "").toLowerCase()) &&
        !Boolean(a.is_system),
    );

    return fallback?.id ? Number(fallback.id) : undefined;
  }

  async function handleImport() {
    setBusy(true);
    setSummary(null);
    setError(null);

    try {
      if (!rows.length) {
        throw new WizardLocalError("onboarding.import.pasteRequired");
      }

      if (kind === "fixed_assets") {
        const accounts = await apiGet<AccountRow[]>(
          `/accounts${companyQuery()}`,
        );

        for (const row of rows) {
          const assetAccount = accounts.find(
            (a) => normKey(a.name) === normKey("Fixed Asset – Other"),
          );

          if (!assetAccount?.id) {
            throw new WizardLocalError("onboarding.import.fixedAssetMissing");
          }

          const assetAccountId = Number(assetAccount.id);

          await apiPost(`/migration/opening-fixed-asset${companyQuery()}`, {
            date: migrationDate,
            asset_account_id: assetAccountId,
            amount: Number(row.net_book_value || 0),
            asset_name: String(row.asset_name || "").trim(),
            remaining_life_months: Number(row.remaining_life_months || 0),
            create_depreciation_schedule: true,
            memo: row.memo || null,
          });
        }

        setSummary({
          created_count: rows.length,
          updated_count: 0,
          skipped_count: 0,
        });
        return;
      }

      if (kind === "prepaids") {
        const bankAccountId = await getMigrationReferenceBankAccountId();

        if (!bankAccountId) {
          throw new WizardLocalError("onboarding.import.prepaidReference");
        }

        for (const row of rows) {
          let vendorId: number | null = null;
          const vendorName = String(row.vendor_name || "").trim();

          if (vendorName) {
            const vendors = await apiGet<VendorRow[]>(
              `/vendors${companyQuery()}`,
            );
            const existing = vendors.find(
              (v) => normKey(v.name) === normKey(vendorName),
            );

            if (existing?.id) {
              vendorId = existing.id;
            } else {
              const created = await apiPost<VendorRow>(
                `/vendors${companyQuery()}`,
                {
                  name: vendorName,
                },
              );
              vendorId = created?.id ? Number(created.id) : null;
            }
          }

          await apiPost(`/migration/opening-prepaid${companyQuery()}`, {
            date: migrationDate,
            amount: Number(row.amount || 0),
            bank_account_id: bankAccountId,
            ...(vendorId ? { vendor_id: vendorId } : {}),
            memo: row.memo || null,
          });
        }

        setSummary({
          created_count: rows.length,
          updated_count: 0,
          skipped_count: 0,
        });
        return;
      }

      if (kind === "deposits") {
        const bankAccountId = await getMigrationReferenceBankAccountId();

        if (!bankAccountId) {
          throw new WizardLocalError("onboarding.import.depositsReference");
        }

        for (const row of rows) {
          const vendorName = String(row.vendor_name || "").trim();

          if (!vendorName) {
            throw new WizardLocalError("onboarding.import.vendorRequired");
          }

          let vendorId: number | null = null;

          const vendors = await apiGet<VendorRow[]>(
            `/vendors${companyQuery()}`,
          );
          const existing = vendors.find(
            (v) => normKey(v.name) === normKey(vendorName),
          );

          if (existing?.id) {
            vendorId = existing.id;
          } else {
            const created = await apiPost<VendorRow>(
              `/vendors${companyQuery()}`,
              {
                name: vendorName,
              },
            );
            vendorId = created?.id ? Number(created.id) : null;
          }

          if (!vendorId) {
            throw new WizardLocalError("onboarding.import.vendorUnavailable", { name: vendorName });
          }

          await apiPost(`/migration/opening-deposit${companyQuery()}`, {
            date: migrationDate,
            direction: "outgoing",
            party_id: vendorId,
            bank_account_id: bankAccountId,
            amount: Number(row.amount || 0),
            memo: null,
          });
        }

        setSummary({
          created_count: rows.length,
          updated_count: 0,
          skipped_count: 0,
        });
        return;
      }

      if (kind === "inventory") {
        // INV-OPEN-1: a persisted batch id + stable per-row client ids so a
        // double-click / retry / reload of the SAME payload replays instead of
        // double-posting the Inventory GL. The attempt is persisted to
        // company-scoped localStorage (survives reload) and cleared only on
        // confirmed success; a changed payload mints a fresh attempt.
        const companyKey = companyId || "default";
        const { batchId, rowIds } = resolveOpeningInventoryAttempt(
          companyKey,
          rows,
          migrationDate,
        );
        const body = {
          batch_id: batchId,
          rows: rows.map((r, i) => ({ ...r, client_row_id: rowIds[i] })),
        };

        const result = await apiPost<any>(
          migrationPath(kind, migrationDate),
          body,
        );

        if (!result?.ok) {
          const rowErrors = (result?.results || [])
            .filter((x: any) => x?.error)
            .map((x: any) => x.error);
          throw new WizardLocalError(
            rowErrors.length ? "onboarding.import.failedDetail" : "onboarding.import.failed",
            rowErrors.length ? { detail: rowErrors.join("; ") } : undefined,
          );
        }

        // Success: this attempt is complete; the next import is a new attempt.
        clearOpeningInventoryAttempt(companyKey);
        setSummary({
          created_count: result.created_count ?? 0,
          updated_count:
            (result.corrected_count ?? 0) +
            (result.unchanged_count ?? 0) +
            (result.replayed_count ?? 0),
          skipped_count: result.error_count ?? 0,
        });
        return;
      }

      const result = await apiPost<MigrationImportSummary>(
        migrationPath(kind, migrationDate),
        rows,
      );

      setSummary(result || {});
    } catch (e: any) {
      setError(errorMessage(e, "onboarding.import.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div className="text-[13px] font-semibold text-gray-700">{title}</div>
      <div className="mt-1 text-[13px] text-gray-600">{helper}</div>

      <textarea
        className="mt-3 w-full min-h-[92px] rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setSummary(null);
          setError(null);
        }}
        placeholder={placeholder}
        aria-label={title}
      />
      <div className="mt-1 text-xs text-gray-500">
        {t("onboarding.import.columnOrder", { columns: columns.map(c => t(c.labelKey)).join(" · ") })}
      </div>

      {rows.length > 0 && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
          <div className="mb-2 text-[11px] font-medium uppercase text-gray-400">
            {t("onboarding.import.preview", { count: rows.length })}
          </div>

          <div className="max-h-32 overflow-y-auto space-y-2 text-[12px] text-gray-600 pr-1">
            {previewRows.map((row, idx) => (
              <div
                key={idx}
                className="whitespace-normal break-words leading-5"
              >
                {columns
                  .map((col) => t("onboarding.import.previewField", { label: t(col.labelKey), value: row[col.key] ?? "" }))
                  .join(" · ")}
              </div>
            ))}
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800">
          {t("onboarding.import.summary", { created: summary.created_count ?? 0, updated: summary.updated_count ?? 0, skipped: summary.skipped_count ?? 0 })}
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
          {messageText(error, t)}
        </div>
      )}

      <button
        type="button"
        className={`${secondaryBtnClass} mt-3`}
        onClick={handleImport}
        disabled={busy || rows.length === 0}
      >
        {busy ? t("onboarding.import.importing") : t("onboarding.import.action", { title })}
      </button>
    </div>
  );
}

function isStripeMethod(pm: PaymentMethod) {
  return (
    pm.provider === "stripe" ||
    ["stripe", "stripe link"].includes((pm.name || "").trim().toLowerCase())
  );
}

// ==== MAIN WIZARD ====

export default function WelcomeWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id?: string }>();

  function companyQuery(suffix: string = "") {
    return company_id ? `?company_id=${company_id}${suffix}` : "";
  }

  async function ensureExistingBusinessServerAccounts(
    accountsIn: Account[],
    useWizardInitialBalance = false,
  ): Promise<Account[]> {
    const existingAccounts = await apiGet<any[]>(`/accounts${companyQuery()}`);

    const findExisting = (acc: Account) =>
      (existingAccounts || []).find(
        (serverAcc) =>
          String(serverAcc.name || "")
            .trim()
            .toLowerCase() ===
            String(acc.name || "")
              .trim()
              .toLowerCase() &&
          String(serverAcc.type || "")
            .trim()
            .toLowerCase() === "asset" &&
          String(serverAcc.kind || "")
            .trim()
            .toLowerCase() ===
            String(acc.kind || "")
              .trim()
              .toLowerCase(),
      );

    const nextAccounts: Account[] = [];

    for (const acc of accountsIn) {
      if (acc.server_id) {
        nextAccounts.push(acc);
        continue;
      }

      const existing = findExisting(acc);
      if (existing?.id) {
        nextAccounts.push({ ...acc, server_id: Number(existing.id) });
        continue;
      }

      const saved = await apiPost<any>(`/accounts${companyQuery()}`, {
        name: acc.name,
        type: "asset",
        kind: acc.kind,
        initial_balance: useWizardInitialBalance ? acc.initial_balance : 0,
      });

      if (!saved?.id) {
        throw new WizardLocalError("onboarding.accounts.creationFailedFor", { name: acc.name });
      }

      nextAccounts.push({ ...acc, server_id: Number(saved.id) });
    }

    return nextAccounts;
  }

  const [step, setStep] = useState(1);

  // STEP 1: Company name
  const [companyName, setCompanyName] = useState("");
  const [companyError, setCompanyError] = useState<WizardMessage | null>(null);
  const [companyField, setCompanyField] = useState("");
  const [businessMode, setBusinessMode] =
    useState<BusinessMode>("new_business");

  // STEP 2: Accounts
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [kind, setKind] = useState<AccountKind>("cash");
  const [accName, setAccName] = useState("");
  const [accBalance, setAccBalance] = useState("");
  const [accountsError, setAccountsError] = useState<WizardMessage | null>(null);

  // STEP 3: Payment Methods
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [pmName, setPmName] = useState(paymentMethodOptions[0].name);
  const [feeRate, setFeeRate] = useState("");
  const [fixedFee, setFixedFee] = useState("");
  const [pmError, setPmError] = useState<WizardMessage | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  // Stripe Connect state
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAcct, setStripeAcct] = useState<string | null>(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [stripeErr, setStripeErr] = useState<WizardMessage | null>(null);
  const [stripeDepositAccountId, setStripeDepositAccountId] =
    useState<string>("");

  // STEP 4: Privacy
  const [privacyEnabled, setPrivacyEnabled] = useState<boolean>(false);
  const [privacyLoading, setPrivacyLoading] = useState<boolean>(false);
  const [privacyErr, setPrivacyErr] = useState<WizardMessage | null>(null);

  // STEP 5: FYE
  const [yearEnd, setYearEnd] = useState<string>("");
  const [fyeError, setFyeError] = useState<WizardMessage | null>(null);
  const [migrationStepComplete, setMigrationStepComplete] = useState(false);
  const [migrationDate] = useState(() => ymdTodayLocal());

  const [showTour, setShowTour] = useState<boolean>(
    () => localStorage.getItem("ricebook_wizard_tour_dismissed") !== "1",
  );

  const stepTipKeys: Record<number, UiKey> = {
    1: "onboarding.tour.company", 2: "onboarding.tour.accounts",
    3: "onboarding.tour.payments", 4: "onboarding.tour.privacy",
    5: "onboarding.tour.yearEnd", 6: "onboarding.tour.review",
  };

  // Saving and error
  const [saving, setSaving] = useState(false);
  const [fatalError, setFatalError] = useState<WizardMessage | null>(null);
  const isExistingBusiness = businessMode === "existing_business";

  // ---- Persist/restore wizard state ----
  // FEE-RATE-SEMANTICS-1 Phase 2B (Finding 4): storage key + restore read are
  // the shared, unit-tested helpers (wizardStorageKey / readWizardRestoreSnapshot).
  // The key was bumped v1 -> v2 because a pre-2B draft persisted payment-method
  // snapshots with a PERCENT-valued `fee_rate` and no `fee_rate_percent`; the new
  // code reads only `fee_rate_percent`, so restoring a v1 draft would submit that
  // method with a silent 0 rate. A v1 draft lives at a DIFFERENT key and is never
  // read again. User-visible consequence: someone with the onboarding wizard open
  // across this deploy re-enters the Business Setup step rather than silently
  // persisting a saved nonzero rate as zero. No raw-rate reader/heuristic added.
  const STORAGE_KEY = wizardStorageKey(company_id);

  const ensureLocalStripeMethods = (accountIdOverride?: number) => {
    const resolvedAccountId =
      accountIdOverride ??
      (stripeDepositAccountId ? Number(stripeDepositAccountId) : undefined);

    if (!resolvedAccountId) return;

    setPaymentMethods((prev) => {
      const next = [...prev];

      const upsertStripeMethod = (
        name: "Stripe" | "Stripe Link",
        providerMethod: "auto" | "paylink",
      ) => {
        const idx = next.findIndex(
          (pm) => (pm.name || "").trim().toLowerCase() === name.toLowerCase(),
        );

        const stripePm: PaymentMethod = {
          name,
          type: "digital",
          fee_rate_percent: 0,
          fixed_fee: 0,
          account_id: resolvedAccountId,
          provider: "stripe",
          provider_method: providerMethod,
          online: true,
        };

        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            ...stripePm,
          };
        } else {
          next.push(stripePm);
        }
      };

      upsertStripeMethod("Stripe", "auto");
      upsertStripeMethod("Stripe Link", "paylink");

      return next;
    });
  };

  const refreshStripeStatus = async (accountIdOverride?: number) => {
    try {
      const status = await getStripeStatus();
      const connected = !!status?.connected && !!status?.account_id;

      setStripeConnected(connected);
      setStripeAcct(status?.account_id || null);

      if (connected) {
        ensureLocalStripeMethods(accountIdOverride);
      }

      return connected;
    } catch {
      setStripeConnected(false);
      setStripeAcct(null);
      return false;
    }
  };

  // Restore when company_id is known; never restore Step 7.
  useEffect(() => {
    if (!company_id) return;

    try {
      // Shared production restore read (v2 key). A pre-2B v1 draft is at a
      // different key and is therefore never restored -- see Finding 4.
      const s = readWizardRestoreSnapshot(company_id) as any;
      if (!s) return;

      if (typeof s.companyName === "string") setCompanyName(s.companyName);
      if (typeof s.companyField === "string") setCompanyField(s.companyField);
      if (
        s.businessMode === "new_business" ||
        s.businessMode === "existing_business"
      ) {
        setBusinessMode(s.businessMode);
      }
      if (typeof s.migrationStepComplete === "boolean") {
        setMigrationStepComplete(s.migrationStepComplete);
      }
      if (Array.isArray(s.accounts)) setAccounts(s.accounts);
      if (Array.isArray(s.paymentMethods)) setPaymentMethods(s.paymentMethods);
      if (typeof s.yearEnd === "string") setYearEnd(s.yearEnd);
      if (typeof s.privacyEnabled === "boolean")
        setPrivacyEnabled(s.privacyEnabled);

      if (typeof s.stripeDepositAccountId === "string") {
        setStripeDepositAccountId(s.stripeDepositAccountId);
      } else if (Array.isArray(s.paymentMethods)) {
        const savedStripePm = s.paymentMethods.find((pm: PaymentMethod) =>
          isStripeMethod(pm),
        );
        if (savedStripePm?.account_id) {
          setStripeDepositAccountId(String(savedStripePm.account_id));
        }
      }

      if (typeof s.step === "number") {
        const safe = Math.min(6, Math.max(1, s.step));
        setStep(safe);
      }
    } catch {
      // ignore parse errors
    }
  }, [company_id, STORAGE_KEY]);

  // Save whenever important fields change
  useEffect(() => {
    if (!company_id) return;

    const snapshot = {
      company_id,
      step,
      companyName,
      companyField,
      businessMode,
      migrationStepComplete,
      accounts,
      paymentMethods,
      stripeDepositAccountId,
      yearEnd,
      privacyEnabled,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // ignore quota errors
    }
  }, [
    company_id,
    STORAGE_KEY,
    step,
    companyName,
    companyField,
    businessMode,
    migrationStepComplete,
    accounts,
    paymentMethods,
    stripeDepositAccountId,
    yearEnd,
    privacyEnabled,
  ]);

  // Handle return from Stripe OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const maybeStep = params.get("step");
    const stripeFlag = params.get("stripe");

    if (maybeStep) {
      const n = parseInt(maybeStep, 10);
      if (!Number.isNaN(n)) {
        setStep(Math.min(6, Math.max(1, n)));
      }
    }

    if (stripeFlag === "done" || stripeFlag === "refresh") {
      void (async () => {
        const pendingDepositId =
          sessionStorage.getItem(STRIPE_WIZARD_DEPOSIT_KEY) ||
          stripeDepositAccountId;

        if (pendingDepositId) {
          setStripeDepositAccountId(pendingDepositId);
        }

        const connected = await refreshStripeStatus(
          pendingDepositId ? Number(pendingDepositId) : undefined,
        );

        if (connected && pendingDepositId) {
          ensureLocalStripeMethods(Number(pendingDepositId));
          sessionStorage.removeItem(STRIPE_WIZARD_DEPOSIT_KEY);
        }

        setConnectingStripe(false);

        const cleaned = new URLSearchParams(location.search);
        cleaned.delete("stripe");
        cleaned.set("step", "3");

        navigate(
          `${location.pathname}${cleaned.toString() ? `?${cleaned.toString()}` : ""}`,
          { replace: true },
        );
      })();
    }
  }, [location.pathname, location.search, navigate, stripeDepositAccountId]);

  // Poll Stripe status when entering Step 3
  useEffect(() => {
    if (step !== 3) return;
    void refreshStripeStatus(
      stripeDepositAccountId ? Number(stripeDepositAccountId) : undefined,
    );
  }, [step, stripeDepositAccountId]);

  // If user changes Stripe deposit account after Stripe is already connected,
  // keep local Stripe methods aligned.
  useEffect(() => {
    if (step !== 3 || !stripeConnected || !stripeDepositAccountId) return;
    ensureLocalStripeMethods(Number(stripeDepositAccountId));
  }, [step, stripeConnected, stripeDepositAccountId]);

  // Load current privacy setting when entering Step 4
  useEffect(() => {
    if (step !== 4) return;
    (async () => {
      setPrivacyLoading(true);
      setPrivacyErr(null);
      try {
        const r = await fetch(`/api/company/privacy${companyQuery()}`, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const txt = await r.text();
        if (!r.ok) throw new Error(txt || `HTTP ${r.status}`);
        let payload: any = null;
        try {
          payload = JSON.parse(txt);
        } catch {
          payload = txt;
        }
        const enabled =
          typeof payload === "object"
            ? !!payload.consent_contribute
            : String(payload).trim().toLowerCase() === "true";
        setPrivacyEnabled(enabled);
      } catch (e: any) {
        setPrivacyErr(errorMessage(e, "privacyControls.errors.load"));
      } finally {
        setPrivacyLoading(false);
      }
    })();
  }, [step]);

  async function startStripeConnect() {
    setStripeErr(null);

    if (!stripeDepositAccountId) {
      setStripeErr(localMessage("stripeConnect.errors.chooseDeposit"));
      return;
    }

    setConnectingStripe(true);

    try {
      sessionStorage.setItem(STRIPE_WIZARD_DEPOSIT_KEY, stripeDepositAccountId);

      const res = await apiPost<ConnectStartResp>(`/stripe/connect/start`, {
        return_to: "welcome",
      });

      if (res?.connected) {
        setStripeConnected(true);
        setStripeAcct(res.account_id || null);
        ensureLocalStripeMethods(Number(stripeDepositAccountId));
        setConnectingStripe(false);
        return;
      }

      if (!res?.url) {
        throw new WizardLocalError("stripeConnect.errors.missingUrl");
      }

      window.location.href = res.url;
    } catch (e: any) {
      setStripeErr(errorMessage(e, "stripeConnect.errors.start"));
      setConnectingStripe(false);
    }
  }

  const handleBack = () => setStep((s) => (s > 1 ? s - 1 : s));

  useEffect(() => {
    if (step !== 7) return;

    const t = setTimeout(() => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }

      try {
        sessionStorage.removeItem(STRIPE_WIZARD_DEPOSIT_KEY);
      } catch {
        // ignore
      }

      const url = company_id ? postLoginPath(company_id, location.search) || "/" : "/";
      window.location.replace(url);
    }, 1200);

    return () => clearTimeout(t);
  }, [step, company_id, STORAGE_KEY, location.search]);

  const handleClose = () => {
    if (step === 1) {
      if (company_id) navigate(`/company/${company_id}/login`);
      else navigate("/login");
    } else {
      setStep(1);
    }
  };

  // STEP 1: Company Name & Industry
  if (step === 1) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-start pt-20 sm:pt-36 px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.welcome")}
          showBack={false}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          <p className={`mb-8 ${helperTextClass}`}>
            {t("onboarding.company.intro")}
          </p>

          <input
            className={`${inputClass} mb-4`}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t("onboarding.company.namePlaceholder")}
            autoFocus
          />

          <div className="mb-4">
            <AppleSelect
              value={companyField}
              onChange={(e) => setCompanyField(e.target.value)}
              required
            >
              <option value="" disabled>
                {t("onboarding.company.chooseIndustry")}
              </option>
              {industryOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </AppleSelect>
          </div>

          <div className="mb-4">
            <div className={labelClass}>{t("onboarding.company.startingPrompt")}</div>

            <div className="grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={() => setBusinessMode("new_business")}
                className={[
                  "text-left rounded-2xl border p-4 transition",
                  businessMode === "new_business"
                    ? "border-appleblue bg-appleblue/10"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100",
                ].join(" ")}
              >
                <div className="text-[13px] font-semibold text-gray-700">
                  {t("onboarding.company.newBusiness")}
                </div>
                <div className="mt-1 text-[13px] text-gray-600">
                  {t("onboarding.company.newBusinessHelp")}
                </div>
              </button>

              <button
                type="button"
                onClick={() => setBusinessMode("existing_business")}
                className={[
                  "text-left rounded-2xl border p-4 transition",
                  businessMode === "existing_business"
                    ? "border-appleblue bg-appleblue/10"
                    : "border-gray-200 bg-gray-50 hover:bg-gray-100",
                ].join(" ")}
              >
                <div className="text-[13px] font-semibold text-gray-700">
                  {t("onboarding.company.existingBusiness")}
                </div>
                <div className="mt-1 text-[13px] text-gray-600">
                  {t("onboarding.company.existingBusinessHelp")}
                </div>
              </button>
            </div>
          </div>

          {companyError && (
            <div className="text-xs text-red-600 mt-2">{messageText(companyError, t)}</div>
          )}

          <Button
            className="w-full h-10"
            onClick={() => {
              if (!companyName.trim()) {
                setCompanyError(localMessage("onboarding.company.nameRequired"));
                return;
              }
              if (!companyField) {
                setCompanyError(localMessage("onboarding.company.industryRequired"));
                return;
              }
              setCompanyError(null);
              setStep(2);
            }}
          >
            {t("onboarding.common.continue")}
          </Button>
        </div>
      </div>
    );
  }

  // STEP 2: Add Cash/Bank Accounts
  if (step === 2) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-start pt-20 sm:pt-36 px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.accounts")}
          onBack={handleBack}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          <p className={`mb-4 ${helperTextClass}`}>
            {isExistingBusiness
              ? t("onboarding.accounts.introExisting")
              : t("onboarding.accounts.introNew")}
          </p>

          {isExistingBusiness && (
            <div className="mb-4 rounded-2xl border border-appleblue/20 bg-appleblue/10 px-4 py-3 text-[13px] text-gray-700">
              {t("onboarding.accounts.equityNotice")}
            </div>
          )}

          <div className="mb-4">
            <label className={labelClass}>{t("directory.products.type")}</label>
            <AppleSelect
              value={kind}
              onChange={(e) => setKind(e.target.value as AccountKind)}
            >
              <option value="cash">{t("onboarding.accounts.cashOption")}</option>
              <option value="bank">{t("onboarding.accounts.bankOption")}</option>
            </AppleSelect>
          </div>

          <div className="mb-4">
            <label className={labelClass}>{t("bankAccounts.accountName")}</label>
            <input
              className={inputClass}
              value={accName}
              onChange={(e) => setAccName(e.target.value)}
              placeholder={t("onboarding.accounts.nameExample")}
            />
          </div>

          <div className="mb-4">
            <label className={labelClass}>
              {isExistingBusiness
                ? t("onboarding.accounts.startDateBalance")
                : t("onboarding.accounts.openingBalance")}
            </label>
            <input
              type="number"
              className={inputClass}
              value={accBalance}
              onChange={(e) => setAccBalance(e.target.value)}
              placeholder="0"
              inputMode="decimal"
            />
          </div>

          <button
            type="button"
            className={`${secondaryBtnClass} mb-3`}
            onClick={() => {
              if (!accName) {
                setAccountsError(localMessage("onboarding.accounts.nameRequired"));
                return;
              }
              if (
                accounts.some(
                  (a) =>
                    a.name.trim().toLowerCase() ===
                    accName.trim().toLowerCase(),
                )
              ) {
                setAccountsError(localMessage("onboarding.accounts.duplicate"));
                return;
              }

              setAccountsError(null);
              setAccounts((prev) => {
                const nextId = prev.length
                  ? Math.max(...prev.map((a) => a.id)) + 1
                  : 1;
                return [
                  ...prev,
                  {
                    id: nextId,
                    kind,
                    name: accName,
                    initial_balance: parseFloat(accBalance) || 0,
                  },
                ];
              });

              setAccName("");
              setAccBalance("");
              setKind("cash");
            }}
          >
            {t("onboarding.accounts.add")}
          </button>

          {accountsError && (
            <div className="text-xs text-red-600 mb-2">{messageText(accountsError, t)}</div>
          )}

          {accounts.length > 0 && (
            <div className="mb-4">
              <h2 className="text-[13px] font-semibold text-gray-700 mb-1">
                {t("onboarding.accounts.added")}
              </h2>

              <ul className="space-y-2 text-[13px] text-gray-700">
                {accounts.map((acc) => {
                  const isServerAccount = Boolean(acc.server_id);

                  return (
                    <li
                      key={acc.id}
                      className="flex justify-between items-center"
                    >
                      <span>
                        {acc.kind === "cash" ? t("balanceSheet.cash") : t("balanceSheet.bank")} —{" "}
                        <span className="font-medium">{acc.name}</span>
                      </span>

                      <span className="flex items-center gap-2">
                        <span className="tabular-nums font-medium text-right min-w-[70px]">
                          ${formatNumber(acc.initial_balance)}
                        </span>

                        <button
                          type="button"
                          className={`${iconBtnClass} ${
                            isServerAccount
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          title={t("directory.common.delete")}
                          disabled={isServerAccount}
                          onClick={() => {
                            if (isServerAccount) return;

                            const nextAccounts = accounts.filter(
                              (a) => a.id !== acc.id,
                            );
                            setAccounts(nextAccounts);

                            if (selectedAccountId === String(acc.id)) {
                              setSelectedAccountId("");
                            }

                            const deletingStripeDeposit =
                              stripeDepositAccountId === String(acc.id);

                            if (deletingStripeDeposit) {
                              setStripeDepositAccountId("");
                            }

                            setPaymentMethods((prev) =>
                              prev.filter((pm) => {
                                if (
                                  pm.account_id === acc.id &&
                                  !isStripeMethod(pm)
                                ) {
                                  return false;
                                }

                                if (
                                  pm.account_id === acc.id &&
                                  isStripeMethod(pm)
                                ) {
                                  return false;
                                }

                                return true;
                              }),
                            );
                          }}
                        >
                          <TrashIcon
                            className={`w-4 h-4 ${
                              isServerAccount
                                ? "text-gray-300"
                                : "text-gray-400"
                            }`}
                          />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <button
            type="button"
            className={primaryBtnClass}
            onClick={async () => {
              if (accounts.length === 0) {
                setAccountsError(localMessage("onboarding.accounts.oneRequired"));
                return;
              }

              try {
                setAccountsError(null);

                if (isExistingBusiness) {
                  const ensured =
                    await ensureExistingBusinessServerAccounts(accounts);
                  setAccounts(ensured);
                }

                setStep(3);
              } catch (err: any) {
                setAccountsError(errorMessage(err, "onboarding.accounts.createFailed"));
              }
            }}
          >
            {t("sales.common.done")}
          </button>
        </div>
      </div>
    );
  }

  // STEP 3: Payment Methods (+ Stripe Connect)
  if (step === 3) {
    const showChargeRate = !["Cash", "On Account", "Other"].includes(pmName);
    const showFixedFee = !["Cash", "On Account", "Other"].includes(pmName);
    const stripeDepositName =
      accounts.find((a) => String(a.id) === stripeDepositAccountId)?.name || "";

    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.payments")}
          onBack={handleBack}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          {/* Stripe Connect card */}
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 mb-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[13px] font-semibold text-gray-700">
                  Stripe Connect
                </div>
                <div className="text-[13px] text-gray-600">
                  {t("onboarding.stripe.description")}
                </div>
              </div>

              <span
                className={[
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none ring-1 shrink-0",
                  stripeConnected
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-gray-100 text-gray-700 ring-gray-200",
                ].join(" ")}
              >
                {stripeConnected ? t("stripeConnect.status.connected") : t("stripeConnect.status.notConnected")}
              </span>
            </div>

            <div className="mt-3">
              <label className={labelClass}>{t("onboarding.stripe.depositAccount")}</label>
              <AppleSelect
                value={stripeDepositAccountId}
                onChange={(e) => {
                  setStripeDepositAccountId(e.target.value);
                  setStripeErr(null);
                }}
                required
              >
                <option value="">{t("onboarding.stripe.selectDeposit")}</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id.toString()}>
                    {acc.name}
                  </option>
                ))}
              </AppleSelect>

              <div className="mt-1 text-xs text-gray-500">
                {t("onboarding.stripe.depositHelp")}
              </div>
            </div>

            {stripeConnected ? (
              <div className="text-[13px] text-gray-600 mt-3 space-y-1">
                <div>
                  {t("onboarding.stripe.connectedAccount")}{" "}
                  <code className="text-gray-800">{stripeAcct || "—"}</code>
                </div>
                <div>
                  {t("stripeConnect.depositsInto")}{" "}
                  <span className="font-medium text-gray-700">
                    {stripeDepositName || t("stripeConnect.notSelected")}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-[13px] text-gray-600 mt-3">
                <Trans i18nKey="onboarding.stripe.chooseThenConnect" components={{ stripe: <span className="font-medium" />, link: <span className="font-medium" /> }} />
              </div>
            )}

            {!!stripeErr && (
              <div className="text-xs text-red-600 mt-2">{messageText(stripeErr, t)}</div>
            )}

            <div className="mt-3 flex flex-col gap-3">
              {!stripeConnected ? (
                <button
                  type="button"
                  onClick={startStripeConnect}
                  className={[
                    "h-10 px-4 rounded-xl bg-black text-white text-[13px] font-medium w-fit",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                  ].join(" ")}
                  disabled={connectingStripe || !stripeDepositAccountId}
                >
                  {connectingStripe ? t("stripeConnect.dialog.connecting") : t("stripeConnect.connect")}
                </button>
              ) : null}

              <div className="text-xs text-gray-500">
                <Trans i18nKey="onboarding.stripe.autoAdd" components={{ stripe: <span className="font-medium" />, link: <span className="font-medium" /> }} />
              </div>
            </div>
          </div>

          <p className={`mb-4 ${helperTextClass}`}>
            {t("onboarding.payments.intro")}
          </p>

          <div className="mb-4">
            <label className={labelClass}>{t("sales.common.paymentMethod")}</label>
            <AppleSelect
              value={pmName}
              onChange={(e) => setPmName(e.target.value)}
            >
              {paymentMethodOptions.map((pm) => (
                <option key={pm.name} value={pm.name}>
                  {paymentOptionKeys[pm.name] ? t(paymentOptionKeys[pm.name]!) : pm.name}
                </option>
              ))}
            </AppleSelect>
          </div>

          {showChargeRate && (
            <div className="mb-4">
              <label className={labelClass}>{t("onboarding.payments.chargeRate")}</label>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={feeRate}
                onChange={(e) => setFeeRate(e.target.value)}
                placeholder={t("paymentMethods.percentExample")}
              />
            </div>
          )}

          {showFixedFee && (
            <div className="mb-4">
              <label className={labelClass}>{t("onboarding.payments.fixedFee")}</label>
              <input
                type="number"
                step="0.01"
                className={inputClass}
                value={fixedFee}
                onChange={(e) => setFixedFee(e.target.value)}
                placeholder={t("onboarding.payments.fixedExample")}
              />
            </div>
          )}

          {accounts.length > 0 && (
            <div className="mb-4">
              <label className={labelClass}>{t("paymentMethods.receivingAccount")}</label>
              <AppleSelect
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                required
              >
                <option value="">{t("paymentMethods.selectReceivingAccount")}</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id.toString()}>
                    {acc.name}
                  </option>
                ))}
              </AppleSelect>
            </div>
          )}

          <button
            type="button"
            className={`${secondaryBtnClass} mb-3`}
            onClick={() => {
              if (!pmName) {
                setPmError(localMessage("onboarding.payments.methodRequired"));
                return;
              }

              if (!selectedAccountId) {
                setPmError(localMessage("paymentMethods.errors.selectReceivingAccount"));
                return;
              }

              if (
                paymentMethods.some(
                  (pm) =>
                    pm.name.trim().toLowerCase() ===
                    pmName.trim().toLowerCase(),
                )
              ) {
                setPmError(localMessage("onboarding.payments.duplicate"));
                return;
              }

              setPmError(null);

              const base: PaymentMethod = {
                name: pmName,
                fee_rate_percent: ["Cash", "On Account", "Other"].includes(pmName)
                  ? 0
                  : Number(feeRate) || 0,
                fixed_fee: ["Cash", "On Account", "Other"].includes(pmName)
                  ? 0
                  : Number(fixedFee) || 0,
                type:
                  paymentMethodOptions.find((pm) => pm.name === pmName)?.type ||
                  "cash",
                account_id: Number(selectedAccountId),
              };

              setPaymentMethods([...paymentMethods, base]);
              setFeeRate("");
              setFixedFee("");
              setPmName(paymentMethodOptions[0].name);
              setSelectedAccountId("");
            }}
          >
            {t("onboarding.payments.add")}
          </button>

          {pmError && (
            <div className="text-xs text-red-600 mb-2">{messageText(pmError, t)}</div>
          )}

          <ul className="space-y-2 text-[13px] text-gray-700">
            {paymentMethods.map((pm, i) => {
              const stripeManaged = isStripeMethod(pm);

              return (
                <li key={i} className="flex justify-between items-center gap-3">
                  <span className="min-w-0">
                    <span className="font-medium">{pm.name}</span>
                    {pm.fee_rate_percent ? ` (${pm.fee_rate_percent}%)` : ""}
                    {pm.fixed_fee ? ` ${t("onboarding.payments.fixedFeeSuffix", { amount: pm.fixed_fee })}` : ""}
                    {stripeManaged && (
                      <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium leading-none ring-1 bg-appleblue/10 text-appleblue ring-appleblue/20">
                        Stripe Connect
                      </span>
                    )}
                  </span>

                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400 min-w-[90px] text-right">
                      {accounts.find((a) => a.id === pm.account_id)?.name || ""}
                    </span>

                    <button
                      className={iconBtnClass}
                      onClick={() =>
                        !stripeManaged &&
                        setPaymentMethods(
                          paymentMethods.filter((_, idx) => idx !== i),
                        )
                      }
                      title={
                        stripeManaged ? t("onboarding.stripe.managed") : t("directory.common.delete")
                      }
                      type="button"
                      disabled={stripeManaged}
                    >
                      <TrashIcon
                        className={`w-4 h-4 ${
                          stripeManaged ? "text-gray-300" : "text-gray-400"
                        }`}
                      />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className={`${primaryBtnClass} mt-4`}
            onClick={() => {
              if (paymentMethods.length === 0) {
                setPmError(localMessage("onboarding.payments.oneRequired"));
                return;
              }

              if (
                stripeConnected &&
                (!stripeDepositAccountId ||
                  !paymentMethods.some(
                    (pm) => isStripeMethod(pm) && pm.account_id > 0,
                  ))
              ) {
                setPmError(localMessage("onboarding.payments.stripeDepositRequired"));
                return;
              }

              setStep(4);
            }}
          >
            {t("sales.common.done")}
          </button>
        </div>
      </div>
    );
  }

  // STEP 4: Privacy
  if (step === 4) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.privacy")}
          onBack={() => setStep(3)}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          <p className={`mb-4 ${helperTextClass}`}>
            {t("onboarding.privacy.description")}
          </p>

          <label className="flex items-start gap-3 border border-gray-200 bg-gray-50 rounded-2xl p-4">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-appleblue"
              checked={privacyEnabled}
              disabled={privacyLoading}
              onChange={(e) => setPrivacyEnabled(e.target.checked)}
            />
            <div>
              <div className="text-[13px] font-medium text-gray-700">
                {t("onboarding.privacy.allow")}
              </div>
              <div className="text-[13px] text-gray-600">
                {t("onboarding.privacy.assurance")}
              </div>
            </div>
          </label>

          {privacyErr && (
            <div className="text-xs text-red-600 mt-2">{messageText(privacyErr, t)}</div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              className={`${secondaryBtnClass} w-1/2`}
              onClick={() => setStep(3)}
              type="button"
            >
              {t("directory.common.back")}
            </button>

            <button
              className={`${primaryBtnClass} w-1/2`}
              type="button"
              onClick={async () => {
                try {
                  await apiPost(`/api/company/privacy${companyQuery()}`, {
                    consent_contribute: privacyEnabled,
                  });
                  setStep(5);
                } catch (e: any) {
                  setPrivacyErr(errorMessage(e, "privacyControls.errors.save"));
                }
              }}
            >
              {t("onboarding.common.continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 5: Financial Year End
  if (step === 5) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.yearEnd")}
          onBack={handleBack}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          <p className={`mb-8 ${helperTextClass}`}>
            {t("onboarding.yearEnd.help")}
          </p>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!yearEnd) {
                setFyeError(localMessage("onboarding.yearEnd.required"));
                return;
              }
              setFyeError(null);
              setStep(6);
            }}
          >
            <div className="mb-6">
              <label className={labelClass}>{t("companyProfile.financialYearEnd")}</label>
              <AppleSelect
                value={yearEnd}
                onChange={(e) => setYearEnd(e.target.value)}
                required
              >
                <option value="">{t("onboarding.yearEnd.selectMonth")}</option>
                <option value="3-31">{t("companyProfile.yearEnds.march")}</option>
                <option value="6-30">{t("companyProfile.yearEnds.june")}</option>
                <option value="9-30">{t("companyProfile.yearEnds.september")}</option>
                <option value="12-31">{t("companyProfile.yearEnds.december")}</option>
              </AppleSelect>
            </div>

            {fyeError && (
              <div className="text-xs text-red-600 mb-4">{messageText(fyeError, t)}</div>
            )}

            <button
              type="submit"
              className={primaryBtnClass}
              disabled={!yearEnd}
            >
              {t("onboarding.common.saveContinue")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // STEP 6A: Existing business migration imports
  if (step === 6 && isExistingBusiness && !migrationStepComplete) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.imports")}
          onBack={handleBack}
          onClose={handleClose}
        />

        <StepProgress step={step} importStage />

        <div className={cardClass}>
          <p className={`mb-3 ${helperTextClass}`}>
            {t("onboarding.import.intro")}
          </p>

          <div className="mb-4 rounded-2xl border border-appleblue/20 bg-appleblue/10 px-4 py-3 text-[13px] text-gray-700">
            {t("onboarding.import.openOnly")}
          </div>

          <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-600">
            {t("onboarding.import.dateLabel")}{" "}
            <span className="font-medium text-gray-700">{formatMMDDYYYYFromYMD(migrationDate)}</span>
          </div>

          <div className="space-y-4">
            {migrationImportSections.map((section) => (
              <MigrationPasteCard
                key={section.kind}
                kind={section.kind}
                title={t(section.titleKey)}
                helper={t(section.helperKey)}
                columns={section.columns}
                migrationDate={migrationDate}
                companyId={company_id}
                wizardAccounts={accounts}
                ensureWizardAccounts={async () => {
                  const ensured =
                    await ensureExistingBusinessServerAccounts(accounts);
                  setAccounts(ensured);
                  return ensured;
                }}
              />
            ))}
          </div>

          <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-600">
            {t("onboarding.import.skipNotice")}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              className={`${secondaryBtnClass} w-1/2`}
              onClick={() => setStep(5)}
            >
              {t("directory.common.back")}
            </button>

            <button
              type="button"
              className={`${primaryBtnClass} w-1/2`}
              onClick={() => setMigrationStepComplete(true)}
            >
              {t("onboarding.common.continue")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 6: Review & Finish
  if (step === 6) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <StepHeader
          title={t("onboarding.titles.review")}
          onBack={handleBack}
          onClose={handleClose}
        />
        <StepProgress step={step} />

        {showTour && (
          <CoachMark
            message={t(stepTipKeys[step])}
            onSkip={() => {
              setShowTour(false);
              localStorage.setItem("ricebook_wizard_tour_dismissed", "1");
            }}
          />
        )}

        <div className={cardClass}>
          <div className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("onboarding.review.setupType")}
              </div>
              <div className="text-[15px] font-semibold text-gray-700">
                {isExistingBusiness
                  ? t("onboarding.company.existingBusiness")
                  : t("onboarding.company.newBusiness")}
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("companyProfile.companyName")}
              </div>
              <div className="text-[15px] font-semibold text-gray-700">
                {companyName}
              </div>
            </div>

            {companyField && (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                  {t("companyProfile.industry")}
                </div>
                <div className="text-[15px] font-semibold text-gray-700">
                  {
                    {
                      fnb: t("onboarding.industries.reviewFnb"),
                      retail: t("onboarding.industries.reviewRetail"),
                      services: t("onboarding.industries.services"),
                      professional: t("onboarding.industries.professional"),
                      online_digital: t("onboarding.industries.reviewOnlineDigital"),
                      other: t("paymentMethods.options.other"),
                    }[companyField]
                  }
                </div>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("onboarding.progress.accounts")}
              </div>
              <div className="space-y-2 text-[13px] text-gray-700">
                {accounts.map((acc) => (
                  <div key={acc.id} className="flex justify-between">
                    <span>
                      {acc.kind === "cash" ? t("balanceSheet.cash") : t("balanceSheet.bank")} —{" "}
                      <span className="font-medium">{acc.name}</span>
                    </span>
                    <span className="tabular-nums font-medium">
                      ${formatNumber(acc.initial_balance)}
                    </span>
                  </div>
                ))}
              </div>

              {isExistingBusiness && (
                <div className="mt-3 text-xs text-gray-500">
                  {t("onboarding.review.openingBalanceNotice")}
                </div>
              )}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("settings.paymentMethods")}
              </div>
              <div className="space-y-2 text-[13px] text-gray-700">
                {paymentMethods.map((pm, i) => (
                  <div key={i} className="flex justify-between gap-3">
                    <span>
                      <span className="font-medium">{pm.name}</span>
                      {pm.fee_rate_percent ? ` (${pm.fee_rate_percent}%)` : ""}
                      {pm.fixed_fee ? ` ${t("onboarding.payments.fixedFeeSuffix", { amount: pm.fixed_fee })}` : ""}
                    </span>
                    <span className="text-xs text-gray-400 min-w-[90px] text-right">
                      {accounts.find((a) => a.id === pm.account_id)?.name || ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("onboarding.titles.privacy")}
              </div>
              <div className="text-[15px] font-semibold text-gray-700">
                {privacyEnabled ? t("onboarding.privacy.allowed") : t("onboarding.privacy.notAllowed")}
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
              <div className="text-gray-500 text-xs mb-1 font-medium uppercase">
                {t("companyProfile.financialYearEnd")}
              </div>
              <div className="text-[15px] font-semibold text-gray-700">
                {{
                  "12-31": t("companyProfile.yearEnds.december"),
                  "3-31": t("companyProfile.yearEnds.march"),
                  "6-30": t("companyProfile.yearEnds.june"),
                  "9-30": t("companyProfile.yearEnds.september"),
                }[yearEnd] || yearEnd}
              </div>
            </div>
          </div>

          {fatalError && (
            <div className="text-xs text-red-600 text-center mt-4 mb-2">
              {messageText(fatalError, t)}
            </div>
          )}

          <button
            className={`${primaryBtnClass} mt-6`}
            onClick={async () => {
              setSaving(true);
              setFatalError(null);

              try {
                // 0) company settings
                await apiPost(`/settings/company`, { value: companyName });
                await apiPost(`/settings/industry`, { value: companyField });
                // 1) create cash/bank accounts, collect server IDs
                // 1) create/reuse cash/bank accounts, collect server IDs
                let finalAccounts: Account[] = [];

                try {
                  finalAccounts = await ensureExistingBusinessServerAccounts(
                    accounts,
                    !isExistingBusiness,
                  );

                  setAccounts(finalAccounts);
                } catch (err: any) {
                  setFatalError(
                    err?.message?.includes("system account")
                      ? { kind: "raw", value: err.message }
                      : localMessage("onboarding.errors.setupFailed", undefined, errorMessage(err, "onboarding.errors.unknown")),
                  );
                  setSaving(false);
                  return;
                }

                // 2) local wizard account id -> real server account id
                const localAccountIdToServerId: Record<number, number> = {};
                for (const acc of finalAccounts) {
                  if (!acc.server_id) {
                    setFatalError(
                      localMessage("onboarding.errors.missingServerId", { name: acc.name }),
                    );
                    setSaving(false);
                    return;
                  }

                  localAccountIdToServerId[acc.id] = acc.server_id;
                }

                if (isExistingBusiness) {
                  for (const acc of finalAccounts) {
                    if (Number(acc.initial_balance || 0) !== 0) {
                      await apiPost(
                        `/migration/opening-cash-bank${companyQuery()}`,
                        {
                          date: new Date().toISOString().slice(0, 10),
                          account_id: acc.server_id,
                          amount: Number(acc.initial_balance || 0),
                          memo: `Opening ${acc.kind} balance - ${acc.name}`,
                        },
                      );
                    }
                  }
                }

                const existingPaymentMethods = await apiGet<any[]>(
                  `/payment-methods${companyQuery()}`,
                );

                const existingPaymentMethodNames = new Set(
                  (existingPaymentMethods || []).map((pm) =>
                    String(pm.name || "").trim().toLowerCase(),
                  ),
                );
                
                // 3) create payment methods with guaranteed integer account_id
                for (const pm of paymentMethods) {
                  const paymentMethodNameKey = String(pm.name || "").trim().toLowerCase();

                  if (existingPaymentMethodNames.has(paymentMethodNameKey)) {
                    continue;
                  }
                  const account_id = localAccountIdToServerId[pm.account_id];
                  
                  if (!account_id) {
                    setFatalError(
                      localMessage("onboarding.errors.receivingMissing", { name: pm.name }),
                    );
                    setSaving(false);
                    return;
                  }

                  // FEE-RATE-SEMANTICS-1 Phase 2B (Finding 3): build via the
                  // SHARED, unit-tested builder -- percent channel (0..100), no
                  // /100 and no bare fee_rate.
                  await apiPost(
                    `/payment-methods${companyQuery()}`,
                    welcomeWizardMethodCreateBody(pm, account_id),
                  );
                }

                // 4) FYE + finish
                await apiPost(`/settings/financial-year-end${companyQuery()}`, {
                  value: yearEnd,
                });

                // Persist privacy (idempotent)
                await apiPost(`/api/company/privacy${companyQuery()}`, {
                  consent_contribute: privacyEnabled,
                });

                // Complete setup
                await apiPost(`/auth/complete-setup`);

                try {
                  localStorage.removeItem(STORAGE_KEY);
                } catch {
                  // ignore
                }

                try {
                  sessionStorage.removeItem(STRIPE_WIZARD_DEPOSIT_KEY);
                } catch {
                  // ignore
                }

                setStep(7);
              } catch {
                setFatalError(localMessage("onboarding.errors.loadFailed"));
                setSaving(false);
              }
            }}
            disabled={saving}
            type="button"
          >
            {saving ? t("directory.common.savingDots") : t("onboarding.common.confirmFinish")}
          </button>
        </div>
      </div>
    );
  }

  // STEP 7: All set!
  if (step === 7) {
    return (
      <div
        className={`max-w-md mx-auto w-full min-h-screen bg-gray-50 ${A.body} flex flex-col justify-center px-2`}
      >
        <div className={`${A.card} p-8 mt-20 text-center`}>
          <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
          <div className={A.title}>{t("onboarding.titles.complete")}</div>
          <div className={`mt-2 ${A.body}`}>
            {t("onboarding.complete.body")}
          </div>

          <div className="flex items-center justify-center mt-6">
            <div className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
